import { useMemo, useState } from "react";
import "./bop020w040.css";
import { useNavigate } from "react-router-dom";
import BOP020W040Template from "./bop020w040.template";
import {
  EXPENSE_TYPE,
  INCOME_TYPE,
  type Category,
  normalizeCategoryLarge,
  normalizeType,
} from "../../common/commonConstant";
import useApiRequest from "../../common/useApiRequest";
import { useMaster } from "../../common/MasterContext";

export type TrendUnit = "month" | "week" | "day";
type DayBasedUnit = Exclude<TrendUnit, "month">;

/** 月別で一度に指定できる期間の上限（月数）。バックエンドの上限と合わせる */
const MAX_MONTH_COUNT = 60;
/** 月別の初期表示の開始年月（当月の何か月前か） */
const DEFAULT_FROM_MONTH_OFFSET = 6;

/**
 * 週別・日別（期間を日付で指定）の設定。
 * - maxDayCount / maxLabel: 一度に指定できる期間の上限（日数）とメッセージ用の表記。
 *   週別は週の始まり・終わりまで広げた後の日数で判定し、104週（728日）がバックエンドの
 *   日別集計の上限（731日）に収まるようにする。日別はグラフが読める点数に収まるよう約3か月に絞る
 * - defaultFromMonthOffset: 集計単位を切り替えた際の開始日（当月の何か月前の1日か）。終了日は当月末日
 */
const DAY_UNIT_SETTINGS: Record<
  DayBasedUnit,
  { maxDayCount: number; maxLabel: string; defaultFromMonthOffset: number }
> = {
  week: { maxDayCount: 104 * 7, maxLabel: "104週", defaultFromMonthOffset: 2 },
  day: { maxDayCount: 92, maxLabel: "92日", defaultFromMonthOffset: 0 },
};

/** グラフ横軸の1区間（月・週・日） */
export interface TrendPeriod {
  key: string;
  /** 金額一覧の見出し用 */
  label: string;
  /** 横軸ラベル用（PC） */
  axisLabel: string;
  /** 横軸ラベル用（SP、短縮表記） */
  shortAxisLabel: string;
}

export type SeriesKind = "total" | "large" | "small";

export interface TrendSeries {
  key: string;
  kind: SeriesKind;
  type: Category;
  label: string;
  categoryLarge: string;
  /** periods と同じ並びの区間別金額 */
  values: number[];
}

interface TrendRow {
  /** 月別: yyyyMM、日別: yyyyMMdd */
  period: string;
  type: string;
  categoryLarge: string | null;
  categorySmall: string | null;
  amount: number;
}

const buildTotalKey = (type: Category) => `total|${type}`;
const buildLargeKey = (type: Category, large: string) =>
  `large|${type}|${large}`;
const buildSmallKey = (type: Category, large: string, small: string) =>
  `small|${type}|${large}|${small}`;

const DEFAULT_VISIBLE_KEYS = [
  buildTotalKey(INCOME_TYPE),
  buildTotalKey(EXPENSE_TYPE),
];

// "yyyy-MM" から n か月ずらした "yyyy-MM" を返す
const shiftMonth = (month: string, diff: number) => {
  const [year, mon] = month.split("-").map(Number);
  const date = new Date(year, mon - 1 + diff, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const countMonths = (from: string, to: string) => {
  const [fromYear, fromMon] = from.split("-").map(Number);
  const [toYear, toMon] = to.split("-").map(Number);
  return (toYear - fromYear) * 12 + (toMon - fromMon) + 1;
};

const pad2 = (value: number) => String(value).padStart(2, "0");

// Date → "yyyyMMdd"（APIの日別集計キーと同じ書式）
const toDayKey = (date: Date) =>
  `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`;

const formatDate = (date: Date) =>
  `${date.getFullYear()}/${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}`;

const formatShortDate = (date: Date) =>
  `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}`;

// "yyyy-MM-dd"（date入力欄の値）→ ローカル時刻のDate
const parseInputDate = (value: string) => {
  const [year, mon, day] = value.split("-").map(Number);
  return new Date(year, mon - 1, day);
};

// Date → "yyyy-MM-dd"（date入力欄の値）
const toInputDate = (date: Date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

// 開始日から終了日までの日数（両端を含む）。時差の影響を受けないようUTCで計算する
const countDays = (from: Date, to: Date) =>
  Math.round(
    (Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) -
      Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) /
      86400000,
  ) + 1;

// 週別の取得期間。週の途中の日付が指定された場合も、その日を含む週全体を集計できるよう
// 開始日はその週の月曜日、終了日はその週の日曜日まで広げる
const expandToWholeWeeks = (fromDate: string, toDate: string) => {
  const from = parseInputDate(fromDate);
  const to = parseInputDate(toDate);
  // getDay(): 日=0, 月=1, …, 土=6 → 月曜起点の経過日数に変換する
  const daysFromMonday = (date: Date) => (date.getDay() + 6) % 7;
  return {
    fromDate: toInputDate(
      new Date(from.getFullYear(), from.getMonth(), from.getDate() - daysFromMonday(from)),
    ),
    toDate: toInputDate(
      new Date(to.getFullYear(), to.getMonth(), to.getDate() + (6 - daysFromMonday(to))),
    ),
  };
};

// 週別・日別に切り替えた際の初期期間（○か月前の1日～当月末日）
const getDefaultDateRange = (unit: DayBasedUnit) => {
  const now = new Date();
  const offset = DAY_UNIT_SETTINGS[unit].defaultFromMonthOffset;
  return {
    fromDate: toInputDate(
      new Date(now.getFullYear(), now.getMonth() - offset, 1),
    ),
    toDate: toInputDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

// 開始日から終了日までの日付を列挙する
const listDays = (start: Date, end: Date) => {
  const days: Date[] = [];
  for (
    let date = start;
    date <= end;
    date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
  ) {
    days.push(date);
  }
  return days;
};

/**
 * 横軸の区間一覧と、APIの集計キー（yyyyMM / yyyyMMdd）→区間インデックスの対応を作る。
 * from/toは月別なら"yyyy-MM"、週別・日別なら"yyyy-MM-dd"。
 * 週別はAPIの日別集計を月曜始まりの週にまとめる（期間は呼び出し元で週全体に広げてある）。
 */
const buildPeriods = (
  unit: TrendUnit,
  from: string,
  to: string,
): { periods: TrendPeriod[]; indexMap: Map<string, number> } => {
  const indexMap = new Map<string, number>();

  if (unit === "month") {
    const periods = Array.from(
      { length: countMonths(from, to) },
      (_, i) => {
        const month = shiftMonth(from, i);
        const [year, mon] = month.split("-");
        indexMap.set(`${year}${mon}`, i);
        return {
          key: month,
          label: `${year}/${mon}`,
          axisLabel: `${year}/${mon}`,
          shortAxisLabel: `${year.slice(2)}/${mon}`,
        };
      },
    );
    return { periods, indexMap };
  }

  const days = listDays(parseInputDate(from), parseInputDate(to));
  if (unit === "day") {
    const periods = days.map((date, i) => {
      indexMap.set(toDayKey(date), i);
      return {
        key: toDayKey(date),
        label: formatDate(date),
        axisLabel: formatShortDate(date),
        shortAxisLabel: formatShortDate(date),
      };
    });
    return { periods, indexMap };
  }

  // 週別: 月曜日（getDay() === 1）ごとに新しい週を始める（期間の初日も月曜日）
  const weeks: Date[][] = [];
  days.forEach((date, i) => {
    if (i === 0 || date.getDay() === 1) weeks.push([]);
    weeks[weeks.length - 1].push(date);
    indexMap.set(toDayKey(date), weeks.length - 1);
  });
  const periods = weeks.map((week) => {
    const start = week[0];
    const end = week[week.length - 1];
    return {
      key: toDayKey(start),
      // 年をまたぐ週は終了日にも年を付ける
      label: `${formatDate(start)}～${
        start.getFullYear() === end.getFullYear()
          ? formatShortDate(end)
          : formatDate(end)
      }`,
      axisLabel: formatShortDate(start),
      shortAxisLabel: formatShortDate(start),
    };
  });
  return { periods, indexMap };
};

/**
 * 機能ID: BOP020w040
 * 機能名: 収支推移画面
 */
const BOP020w040 = () => {
  const getDefaultFromMonth = () =>
    shiftMonth(getCurrentMonth(), -DEFAULT_FROM_MONTH_OFFSET);

  const [unit, setUnit] = useState<TrendUnit>("month");
  // 月別は年月（yyyy-MM）、週別・日別は日付（yyyy-MM-dd）で期間を指定する
  const [fromMonth, setFromMonth] = useState(getDefaultFromMonth);
  const [toMonth, setToMonth] = useState(getCurrentMonth);
  const [fromDate, setFromDate] = useState(
    () => getDefaultDateRange("week").fromDate,
  );
  const [toDate, setToDate] = useState(() => getDefaultDateRange("week").toDate);
  // 検索実行時点の区間一覧（入力欄を変更しても、再検索するまでグラフの横軸は変えない）
  const [periods, setPeriods] = useState<TrendPeriod[]>([]);
  const [periodIndexMap, setPeriodIndexMap] = useState<Map<string, number>>(
    () => new Map(),
  );
  const [rows, setRows] = useState<TrendRow[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(
    () => new Set(DEFAULT_VISIBLE_KEYS),
  );
  const navigate = useNavigate();
  const { isLoading, execute } = useApiRequest<TrendRow[]>();
  const { categoryLargeList, categorySmallList } = useMaster();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handleClear = () => {
    setUnit("month");
    setFromMonth(getDefaultFromMonth());
    setToMonth(getCurrentMonth());
    const defaultRange = getDefaultDateRange("week");
    setFromDate(defaultRange.fromDate);
    setToDate(defaultRange.toDate);
    setPeriods([]);
    setPeriodIndexMap(new Map());
    setRows([]);
    setHasSearched(false);
  };

  // 週別・日別に切り替えた際は、その集計単位に適した長さの期間（日付）を初期値として設定する。
  // 月別の年月はそのまま保持する
  const handleUnitChange = (nextUnit: TrendUnit) => {
    setUnit(nextUnit);
    if (nextUnit !== "month") {
      const defaultRange = getDefaultDateRange(nextUnit);
      setFromDate(defaultRange.fromDate);
      setToDate(defaultRange.toDate);
    }
  };

  // 実際にAPIへ渡す日付の期間（週別は週全体まで広げる）
  const getEffectiveDateRange = () =>
    unit === "week" ? expandToWholeWeeks(fromDate, toDate) : { fromDate, toDate };

  // 入力チェック。問題があればエラーメッセージを返す
  const validatePeriod = () => {
    if (unit === "month") {
      if (!fromMonth || !toMonth) return "開始年月と終了年月を入力してください";
      const monthCount = countMonths(fromMonth, toMonth);
      if (monthCount < 1) return "開始年月は終了年月以前を指定してください";
      if (monthCount > MAX_MONTH_COUNT) {
        return `期間は最大${MAX_MONTH_COUNT}か月で指定してください`;
      }
      return null;
    }
    if (!fromDate || !toDate) return "開始日と終了日を入力してください";
    if (fromDate > toDate) return "開始日は終了日以前を指定してください";
    const range = getEffectiveDateRange();
    const dayCount = countDays(
      parseInputDate(range.fromDate),
      parseInputDate(range.toDate),
    );
    const { maxDayCount, maxLabel } = DAY_UNIT_SETTINGS[unit];
    if (dayCount > maxDayCount) {
      return `期間は最大${maxLabel}で指定してください`;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errorMessage = validatePeriod();
    if (errorMessage) {
      alert(errorMessage);
      return;
    }

    const isMonthUnit = unit === "month";
    const dateRange = getEffectiveDateRange();
    const result = await execute(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop020rs04001/search`,
      {
        method: "POST",
        body: JSON.stringify(
          isMonthUnit
            ? {
                unit: "month",
                fromMonth: fromMonth.replace("-", ""),
                toMonth: toMonth.replace("-", ""),
              }
            : {
                // 週別はAPIの日別集計を画面側で週単位にまとめる
                unit: "day",
                fromDate: dateRange.fromDate.replace(/-/g, ""),
                toDate: dateRange.toDate.replace(/-/g, ""),
              },
        ),
      },
    );

    if (!result.ok) {
      alert(result.error.message);
      return;
    }

    const built = isMonthUnit
      ? buildPeriods(unit, fromMonth, toMonth)
      : buildPeriods(unit, dateRange.fromDate, dateRange.toDate);
    setPeriods(built.periods);
    setPeriodIndexMap(built.indexMap);
    setRows(Array.isArray(result.data) ? result.data : []);
    setHasSearched(true);
  };

  // 汎用マスタの並び順（項目管理画面の表示順）を系列の並び順に使う
  const { largeOrderMap, smallOrderMap } = useMemo(() => {
    const largeMap = new Map<string, number>();
    categoryLargeList.forEach((item, index) => {
      const key = buildLargeKey(
        normalizeType(item.type),
        normalizeCategoryLarge(item.category),
      );
      if (!largeMap.has(key)) largeMap.set(key, index);
    });
    const smallMap = new Map<string, number>();
    categorySmallList.forEach((item, index) => {
      const key = buildSmallKey(
        normalizeType(item.type),
        normalizeCategoryLarge(item.category),
        item.label,
      );
      if (!smallMap.has(key)) smallMap.set(key, index);
    });
    return { largeOrderMap: largeMap, smallOrderMap: smallMap };
  }, [categoryLargeList, categorySmallList]);

  const series = useMemo<TrendSeries[]>(() => {
    const seriesMap = new Map<string, TrendSeries>();
    const ensureSeries = (
      key: string,
      kind: SeriesKind,
      type: Category,
      label: string,
      categoryLarge: string,
    ) => {
      let target = seriesMap.get(key);
      if (!target) {
        target = {
          key,
          kind,
          type,
          label,
          categoryLarge,
          values: periods.map(() => 0),
        };
        seriesMap.set(key, target);
      }
      return target;
    };

    // 合計はデータがなくても常に表示対象にする
    ([INCOME_TYPE, EXPENSE_TYPE] as Category[]).forEach((type) =>
      ensureSeries(buildTotalKey(type), "total", type, `${type}合計`, ""),
    );

    rows.forEach((row) => {
      const index = periodIndexMap.get(row.period);
      if (index === undefined) return;
      const type = normalizeType(row.type);
      const large = normalizeCategoryLarge(row.categoryLarge || "");
      const small = row.categorySmall || "";
      const amount = Number(row.amount) || 0;

      ensureSeries(
        buildTotalKey(type),
        "total",
        type,
        `${type}合計`,
        "",
      ).values[index] += amount;
      if (!large) return;
      ensureSeries(
        buildLargeKey(type, large),
        "large",
        type,
        large,
        large,
      ).values[index] += amount;
      if (!small) return;
      ensureSeries(
        buildSmallKey(type, large, small),
        "small",
        type,
        small,
        large,
      ).values[index] += amount;
    });

    const getOrder = (item: TrendSeries) => {
      if (item.kind === "total") return -1;
      const map = item.kind === "large" ? largeOrderMap : smallOrderMap;
      return map.get(item.key) ?? Number.MAX_SAFE_INTEGER;
    };
    const typeOrder = (type: Category) => (type === INCOME_TYPE ? 0 : 1);

    // 支出が負数で登録されている場合もあるため、グラフは絶対値で描く（月次集計画面と同じ扱い）
    return Array.from(seriesMap.values())
      .map((item) => ({
        ...item,
        values: item.values.map((value) => Math.abs(value)),
      }))
      .sort(
        (a, b) =>
          typeOrder(a.type) - typeOrder(b.type) ||
          getOrder(a) - getOrder(b) ||
          a.label.localeCompare(b.label, "ja"),
      );
  }, [periods, periodIndexMap, rows, largeOrderMap, smallOrderMap]);

  const handleToggleSeries = (key: string, visible: boolean) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (visible) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const handleToggleGroup = (keys: string[], visible: boolean) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => (visible ? next.add(key) : next.delete(key)));
      return next;
    });
  };

  const handleHideAll = () => setVisibleKeys(new Set());

  return (
    <BOP020W040Template
      unit={unit}
      fromMonth={fromMonth}
      toMonth={toMonth}
      isLoading={isLoading}
      showResults={hasSearched}
      periods={periods}
      series={series}
      visibleKeys={visibleKeys}
      onUnitChange={handleUnitChange}
      onFromMonthChange={setFromMonth}
      onToMonthChange={setToMonth}
      fromDate={fromDate}
      toDate={toDate}
      onFromDateChange={setFromDate}
      onToDateChange={setToDate}
      onClear={handleClear}
      onBack={handleBack}
      onSubmit={handleSubmit}
      onToggleSeries={handleToggleSeries}
      onToggleGroup={handleToggleGroup}
      onHideAll={handleHideAll}
    />
  );
};

export default BOP020w040;
